/**
 * Direct public URL download with retry support.
 * - Reports progress as 0..100 when total is known; otherwise null while downloading.
 * - Retries on failure; throws after retries exhausted (so caller can mark ❌).
 */
const onClickDownloadVideo = async (
  downloadFileName,
  downloadFileNameExtension,
  finalFileURL,
  onProgress,             // function(percent:number|null)
  retryCount = 3,
  abortSignal             // optional AbortSignal
) => {
  const fileExtension = (downloadFileNameExtension || "").replace(/^\./, "");
  const delay = (ms) => new Promise((res) => setTimeout(res, ms));
  
  try {
    const response = await fetch(finalFileURL, {
      signal: abortSignal,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const contentLength = response.headers.get("content-length");
    const total = contentLength ? parseInt(contentLength, 10) : null;

    const reader = response.body.getReader();
    const chunks = [];
    let loaded = 0;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      chunks.push(value);
      loaded += value.length;

      if (total && typeof onProgress === "function") {
        const percentage = Math.round((loaded / total) * 100);
        onProgress(percentage);
      } else if (typeof onProgress === "function") {
        onProgress(null); // indicate "in progress" but unknown percent
      }
    }

    // Build filename
    let finalName = downloadFileName || "video";
    const hasDot = /\.[a-z0-9]+$/i.test(finalName);
    if (!hasDot && fileExtension) {
      finalName = `${finalName}.${fileExtension}`;
    }

    const blob = new Blob(chunks, {
      type: response.headers.get("content-type") || "application/octet-stream",
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = finalName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);

    // ensure final 100 when total was unknown
    if (typeof onProgress === "function") onProgress(100);
  } catch (error) {
    if (abortSignal?.aborted) {
      throw error instanceof Error ? error : new Error("Download aborted");
    }

    if (retryCount > 0) {
      console.warn(
        `onClickDownloadVideo: error (${error?.message || error}). Retrying... attempts left: ${retryCount}`
      );
      await delay(1000);
      return onClickDownloadVideo(
        downloadFileName,
        downloadFileNameExtension,
        finalFileURL,
        onProgress,
        retryCount - 1,
        abortSignal
      );
    }

    const finalErr = error instanceof Error ? error : new Error(String(error || "Download failed"));
    console.error("onClickDownloadVideo: all retry attempts failed.", finalErr);
    throw finalErr;
  }
};

/**
 * UI handler that:
 * - Normalizes the filename
 * - Calls onClickDownloadVideo
 * - Updates your downloadProgress store keyed by finalFileURL
 *
 * NOTE: This version writes an OBJECT with {status, percent} so your Sidebar shows ❌ correctly.
 */
const handleDownloadClick = async (
  e,
  fileName,
  fileExtension,
  finalFileURL,
  setDownloadProgress,
  videoSasToken = "",
  nonVideoSasToken = "",
  createDialogBox,
  closeDialogBox,
  abortSignal, // optional
  fetchVideoSasToken, // function to refresh video SAS token
  fetchNonVideoSasToken, // function to refresh non-video SAS token
  showAccessTokenExpiredDialog,
  fileSize = ""
) => {

  const VIDEO_EXTENSIONS = ["mp4", "avi", "mov", "mkv", "flv", "wmv", "webm", "m4v", "hevc"];
  const isVideoFile = (ext) => !!(ext && VIDEO_EXTENSIONS.includes(ext.toLowerCase()));

  const appendSasTokenToUrl = (url, ext = "") => {
    if (!url) return url;
    const token = isVideoFile(ext) ? videoSasToken : nonVideoSasToken;
    if (!token) return url;
    // const separator = url.includes("?") ? "&" : "";
    //console.log(`${url}${token}`);
    return `${url}${token}`;
  };

  const handleAuthFailure = () => {
    if (createDialogBox && closeDialogBox) {
      createDialogBox(
        "Session expired",
        "Your session has expired due to inactivity.",
        [
          {
            type: "button",
            text: "Extend session",
            attributes: { id: "dialog-box-extend-button" },
            listener: {
            event: "click",
            callback: async () => {
              if (closeDialogBox) closeDialogBox();
              // Clear all failed downloads before refreshing tokens
              setDownloadProgress((prev) => {
                const updated = { ...prev };
                Object.keys(updated).forEach((key) => {
                  if (updated[key]?.status === "failed") {
                    delete updated[key];
                  }
                });
                return updated;
              });
              await fetchVideoSasToken();
              await fetchNonVideoSasToken();
            },
            },
          },
        ],
        () => {
        if (closeDialogBox) closeDialogBox();
        window.location.reload();
      },
    );
    }
  };

  e.preventDefault();

  // Split the file name and extension (keep your original logic)
  const [baseName, ext] = fileName.split(".").reduce(
    (acc, part, index, arr) => {
      if (index === arr.length - 1) {
        acc[1] = part;
      } else {
        acc[0] += (index ? "." : "") + part;
      }
      return acc;
    },
    ["", ""]
  );

  // If provided fileName already ends with the same extension, use baseName (avoid duplicate .ext)
  const finalFileName = ext === fileExtension ? baseName : fileName;
  const fileMeta = {
    fileName: finalFileName || fileName || "Unknown File",
    ext: fileExtension || "",
    fileSize: fileSize ?? "",
  };

  // determine whether this is a video file (use same list as DownloadButton)
  const downloadUrl = appendSasTokenToUrl(finalFileURL, fileExtension);

  // Mark started using original URL as key
  setDownloadProgress((prev) => ({
    ...prev,
    [finalFileURL]: { status: "in-progress", percent: 0, ...fileMeta },
  }));

  try {
    await onClickDownloadVideo(
      finalFileName,
      fileExtension,
      downloadUrl,
      (progress) => {
        // progress can be a number or null (unknown total)        
        setDownloadProgress((prev) => ({
          ...prev,
          [finalFileURL]:
            progress == null
              ? { status: "in-progress", ...fileMeta } // no percent when unknown
              : {
                  status: progress === 100 ? "completed" : "in-progress",
                  percent: progress,
                  ...fileMeta,
                },
        }));
      },
      3,
      abortSignal
    );

    // final guard: ensure completed if onProgress didn't send 100
    setDownloadProgress((prev) => ({
      ...prev,
      [finalFileURL]: { status: "completed", percent: 100, ...fileMeta },
    }));
  } catch (err) {
    const msg = err?.message || "";
    // Check for session timeout (403 errors)
    if (msg.includes("HTTP 403")) {
      handleAuthFailure();
    }
    if (msg.includes("HTTP 401")) {
      showAccessTokenExpiredDialog();
    }
    // Mark ❌ Failed so Sidebar can render failure state
    setDownloadProgress((prev) => ({
      ...prev,
      [finalFileURL]: {
        status: "failed",
        error: err?.message || "Download failed",
        ...fileMeta,
      },
    }));
  }
};

export default handleDownloadClick;
